import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Form, Modal, Spinner, Alert, Badge, Table } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { colors } from '../../brand-colors';

/**
 * Command Center Bonuses Management
 * Value-add items that aren't sold separately but add perceived value to promotions
 */
function CCBonuses() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bonuses, setBonuses] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingBonus, setEditingBonus] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchBonuses();
  }, []);

  const fetchBonuses = async () => {
    try {
      const res = await fetch('/api/bonuses', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBonuses(data.bonuses || []);
      }
    } catch (err) {
      setError('Failed to load bonuses');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingBonus({
      name: '',
      description: '',
      perceived_value: ''
    });
    setShowModal(true);
  };

  const openEditModal = (bonus) => {
    setEditingBonus({ ...bonus });
    setShowModal(true);
  };

  const saveBonus = async () => {
    if (!editingBonus.name?.trim()) {
      setError('Bonus name is required');
      return;
    }

    setSaving(true);
    try {
      const isNew = !editingBonus.id;
      const url = isNew ? '/api/bonuses' : `/api/bonuses/${editingBonus.id}`;
      const method = isNew ? 'POST' : 'PUT';

      const response = await fetch(url, {
        method,
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editingBonus)
      });

      if (response.ok) {
        setShowModal(false);
        setSuccess(isNew ? 'Bonus created!' : 'Bonus updated!');
        setTimeout(() => setSuccess(''), 3000);
        fetchBonuses();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to save');
      }
    } catch (err) {
      setError('Error saving bonus');
    } finally {
      setSaving(false);
    }
  };

  const deleteBonus = async (bonus) => {
    if (!window.confirm(`Delete "${bonus.name}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(`/api/bonuses/${bonus.id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        setSuccess('Bonus deleted');
        setTimeout(() => setSuccess(''), 3000);
        fetchBonuses();
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to delete');
      }
    } catch (err) {
      setError('Error deleting bonus');
    }
  };

  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return `$${parseFloat(amount).toLocaleString()}`;
  };

  const totalPerceivedValue = bonuses.reduce((sum, b) => sum + (parseFloat(b.perceived_value) || 0), 0);

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" style={{ color: colors.goldMid }} />
        <p className="mt-3">Loading bonuses...</p>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <Button
            variant="link"
            className="p-0 mb-2"
            onClick={() => navigate('/command-center/products')}
          >
            ← Back to Products
          </Button>
          <h1 className="mb-1">Bonuses</h1>
          <p className="text-muted mb-0">
            Value-add items that sweeten your offers. Not sold separately but add perceived value to promotions.
          </p>
        </div>
        <Button
          variant="dark"
          style={{ backgroundColor: colors.greyDarkest }}
          onClick={openCreateModal}
        >
          + New Bonus
        </Button>
      </div>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {/* Info Card */}
      <Card className="mb-4 border-0 shadow-sm" style={{ backgroundColor: '#fef3f8', borderLeft: `4px solid #e83e8c` }}>
        <Card.Body>
          <Row className="align-items-center">
            <Col md={1} className="text-center">
              <span style={{ fontSize: '2rem' }}>💡</span>
            </Col>
            <Col md={11}>
              <strong>Pro Tip:</strong> Bonuses are powerful tools for increasing perceived value without discounting.
              Use them to make your offers irresistible. Common bonuses include templates, checklists, bonus training,
              community access, or 1-on-1 sessions.
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Bonuses Table */}
      {bonuses.length === 0 ? (
        <Card className="shadow-sm">
          <Card.Body className="text-center py-5">
            <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎀</div>
            <h4>No Bonuses Yet</h4>
            <p className="text-muted mb-4">
              Bonuses add perceived value to your offers without lowering your price.
              Create bonuses to use in promotions and special offers.
            </p>
            <Button
              variant="dark"
              style={{ backgroundColor: colors.greyDarkest }}
              onClick={openCreateModal}
            >
              Create Your First Bonus
            </Button>
          </Card.Body>
        </Card>
      ) : (
        <Card className="shadow-sm">
          <Table responsive hover className="mb-0">
            <thead style={{ backgroundColor: colors.greyLightest }}>
              <tr>
                <th>Bonus Name</th>
                <th>Description</th>
                <th>Perceived Value</th>
                <th className="text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {bonuses.map(bonus => (
                <tr key={bonus.id}>
                  <td>
                    <strong>{bonus.name}</strong>
                  </td>
                  <td>
                    {bonus.description ? (
                      <span style={{ maxWidth: '400px', display: 'block' }}>
                        {bonus.description.substring(0, 100)}{bonus.description.length > 100 ? '...' : ''}
                      </span>
                    ) : (
                      <span className="text-muted">No description</span>
                    )}
                  </td>
                  <td>
                    {bonus.perceived_value ? (
                      <Badge bg="success" className="fs-6">
                        {formatCurrency(bonus.perceived_value)} value
                      </Badge>
                    ) : (
                      <span className="text-muted">Not set</span>
                    )}
                  </td>
                  <td className="text-center">
                    <Button variant="link" size="sm" onClick={() => openEditModal(bonus)}>
                      Edit
                    </Button>
                    <Button
                      variant="link"
                      size="sm"
                      className="text-danger"
                      onClick={() => deleteBonus(bonus)}
                    >
                      Delete
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {/* Stats */}
      {bonuses.length > 0 && (
        <Row className="mt-4">
          <Col md={6}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#e83e8c' }}>
                  {bonuses.length}
                </div>
                <div className="text-muted">Total Bonuses</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="text-center border-0 shadow-sm">
              <Card.Body>
                <div style={{ fontSize: '2rem', fontWeight: 'bold', color: '#28a745' }}>
                  {formatCurrency(totalPerceivedValue)}
                </div>
                <div className="text-muted">Total Perceived Value</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Bonus Ideas */}
      {bonuses.length === 0 && (
        <Card className="mt-4 shadow-sm">
          <Card.Header style={{ backgroundColor: colors.greyLightest }}>
            <strong>Bonus Ideas to Get You Started</strong>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={4}>
                <h6>Templates & Swipe Files</h6>
                <ul className="text-muted small">
                  <li>Email templates</li>
                  <li>Social media templates</li>
                  <li>Spreadsheet calculators</li>
                  <li>Checklists & cheat sheets</li>
                </ul>
              </Col>
              <Col md={4}>
                <h6>Access & Community</h6>
                <ul className="text-muted small">
                  <li>Private community access</li>
                  <li>Monthly Q&A calls</li>
                  <li>Accountability group</li>
                  <li>Exclusive events</li>
                </ul>
              </Col>
              <Col md={4}>
                <h6>Personal Attention</h6>
                <ul className="text-muted small">
                  <li>Strategy session</li>
                  <li>Profile review</li>
                  <li>Hot seat coaching</li>
                  <li>Priority support</li>
                </ul>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Create/Edit Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton style={{ backgroundColor: colors.greyDarkest, color: colors.goldLight }}>
          <Modal.Title>{editingBonus?.id ? 'Edit Bonus' : 'New Bonus'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingBonus && (
            <Form>
              <Form.Group className="mb-3">
                <Form.Label><strong>Bonus Name *</strong></Form.Label>
                <Form.Control
                  type="text"
                  placeholder="e.g., 'Private Community Access' or 'Bonus Training Module'"
                  value={editingBonus.name || ''}
                  onChange={(e) => setEditingBonus({ ...editingBonus, name: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Make it sound valuable! This is what customers will see in your offers.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Description</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  placeholder="Describe what this bonus includes and why it's valuable..."
                  value={editingBonus.description || ''}
                  onChange={(e) => setEditingBonus({ ...editingBonus, description: e.target.value })}
                />
                <Form.Text className="text-muted">
                  Help customers understand the value. Be specific about what they get.
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Perceived Value</strong></Form.Label>
                <div className="input-group" style={{ maxWidth: '200px' }}>
                  <span className="input-group-text">$</span>
                  <Form.Control
                    type="number"
                    step="0.01"
                    placeholder="e.g., 97"
                    value={editingBonus.perceived_value || ''}
                    onChange={(e) => setEditingBonus({ ...editingBonus, perceived_value: e.target.value })}
                  />
                </div>
                <Form.Text className="text-muted">
                  The value shown in marketing (e.g., "$97 value!"). This helps with price anchoring in your offers.
                </Form.Text>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Cancel
          </Button>
          <Button
            variant="dark"
            style={{ backgroundColor: colors.greyDarkest }}
            onClick={saveBonus}
            disabled={saving}
          >
            {saving ? <Spinner animation="border" size="sm" /> : (editingBonus?.id ? 'Save Changes' : 'Create Bonus')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default CCBonuses;
